/**
 * AIBioGenerator - Claude-powered artist bio generator for EPK
 * Streams AI-generated bio with tone selection
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Sparkles, Wand2, Save, RotateCcw, X, Check, 
  Briefcase, Coffee, Flame, BookOpen 
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tone options with icons and descriptions
const TONE_OPTIONS = [
  { 
    id: 'professional', 
    label: 'Professional', 
    icon: Briefcase, 
    description: 'Industry-ready, polished' 
  },
  { 
    id: 'casual', 
    label: 'Casual', 
    icon: Coffee, 
    description: 'Friendly, approachable' 
  },
  { 
    id: 'edgy', 
    label: 'Edgy', 
    icon: Flame, 
    description: 'Bold, artistic' 
  },
  { 
    id: 'storytelling', 
    label: 'Story', 
    icon: BookOpen, 
    description: 'Narrative, personal' 
  }
];

const AIBioGenerator = ({ currentBio, onBioSaved, onClose }) => {
  const [selectedTone, setSelectedTone] = useState('professional');
  const [maxLength, setMaxLength] = useState(200);
  const [generatedBio, setGeneratedBio] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  // Generate bio using Claude
  const handleGenerate = async () => {
    setIsGenerating(true);
    setGeneratedBio('');
    setError(null);
    setSaved(false);

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please log in to generate a bio');
      setIsGenerating(false);
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/artists/epk/generate-bio`,
        {
          tone: selectedTone,
          max_length: maxLength
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data?.success && response.data?.bio) {
        setGeneratedBio(response.data.bio);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('Error generating bio:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to generate bio');
    } finally {
      setIsGenerating(false);
    }
  };

  // Save the generated bio
  const handleSave = async () => {
    if (!generatedBio.trim()) return;

    setIsSaving(true);
    setError(null);

    const token = localStorage.getItem('token');

    try {
      await axios.post(
        `${API_URL}/api/artists/epk/save-generated-bio`,
        generatedBio.trim(),
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setSaved(true);
      if (onBioSaved) {
        onBioSaved(generatedBio.trim());
      }

      // Auto-close after save
      setTimeout(() => {
        if (onClose) onClose();
      }, 1500);
    } catch (err) {
      console.error('Error saving bio:', err);
      setError(err.response?.data?.detail || 'Failed to save bio');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className="w-full max-w-2xl bg-[#12101A] rounded-3xl border border-white/10 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-white/5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF007F] to-[#00F0FF] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">AI Bio Generator</h3>
                <p className="text-[#71717A] text-sm">Powered by Claude</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/5 transition-colors"
            >
              <X className="w-5 h-5 text-[#71717A]" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Tone Selection */}
          <div>
            <label className="text-white text-sm font-medium mb-3 block">
              Select Tone
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {TONE_OPTIONS.map((tone) => {
                const Icon = tone.icon;
                const isSelected = selectedTone === tone.id;
                return (
                  <button
                    key={tone.id}
                    onClick={() => setSelectedTone(tone.id)}
                    disabled={isGenerating}
                    className={`p-3 rounded-xl border transition-all ${
                      isSelected
                        ? 'border-[#FF007F] bg-[#FF007F]/10'
                        : 'border-white/10 bg-white/5 hover:border-white/20'
                    } ${isGenerating ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Icon className={`w-5 h-5 mx-auto mb-1 ${
                      isSelected ? 'text-[#FF007F]' : 'text-[#71717A]'
                    }`} />
                    <p className={`text-sm font-medium ${
                      isSelected ? 'text-white' : 'text-[#A1A1AA]'
                    }`}>{tone.label}</p>
                    <p className="text-[10px] text-[#71717A] mt-0.5">{tone.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Length Slider */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-white text-sm font-medium">
                Max Length
              </label>
              <span className="text-[#00F0FF] text-sm font-medium">{maxLength} words</span>
            </div>
            <input
              type="range"
              min="50"
              max="500"
              step="50"
              value={maxLength}
              onChange={(e) => setMaxLength(Number(e.target.value))}
              disabled={isGenerating}
              className="w-full h-2 bg-white/10 rounded-full appearance-none cursor-pointer
                [&::-webkit-slider-thumb]:appearance-none
                [&::-webkit-slider-thumb]:w-4
                [&::-webkit-slider-thumb]:h-4
                [&::-webkit-slider-thumb]:rounded-full
                [&::-webkit-slider-thumb]:bg-[#FF007F]
                [&::-webkit-slider-thumb]:cursor-pointer
                disabled:opacity-50"
            />
          </div>

          {/* Current Bio Preview */}
          {currentBio && !generatedBio && (
            <div className="p-4 rounded-xl bg-white/5 border border-white/10">
              <p className="text-[#71717A] text-xs mb-2">Current Bio:</p>
              <p className="text-[#A1A1AA] text-sm line-clamp-3">{currentBio}</p>
            </div>
          )}

          {/* Generated Bio */}
          {(generatedBio || isGenerating) && (
            <div 
              className="p-4 rounded-xl bg-white/5 border border-white/10 min-h-[150px] max-h-[250px] overflow-y-auto"
            >
              {isGenerating ? (
                <div className="flex items-center justify-center h-32">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 border-2 border-[#FF007F] border-t-transparent rounded-full animate-spin" />
                    <p className="text-[#A1A1AA]">Claude is writing your bio...</p>
                  </div>
                </div>
              ) : (
                <p className="text-white leading-relaxed">{generatedBio}</p>
              )}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {saved && (
            <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/30 flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              <p className="text-green-400 text-sm">Bio saved successfully!</p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-white/5 flex items-center gap-3">
          {!generatedBio && !isGenerating ? (
            <button
              onClick={handleGenerate}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl 
                bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold
                hover:opacity-90 transition-opacity"
              data-testid="generate-bio-btn"
            >
              <Wand2 className="w-5 h-5" />
              Generate Bio
            </button>
          ) : (
            <>
              <button
                onClick={handleGenerate}
                disabled={isGenerating}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl 
                  bg-white/5 border border-white/10 text-white font-medium
                  hover:bg-white/10 transition-colors disabled:opacity-50"
              >
                <RotateCcw className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
                Regenerate
              </button>
              <button
                onClick={handleSave}
                disabled={isGenerating || isSaving || !generatedBio.trim() || saved}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl 
                  bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold
                  hover:opacity-90 transition-opacity disabled:opacity-50"
                data-testid="save-bio-btn"
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : saved ? (
                  <>
                    <Check className="w-5 h-5" />
                    Saved!
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Save Bio
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AIBioGenerator;
